// Weather particles (PLR-10) — client-only (ENG-1): a cheap point cloud of rain
// or snow that follows the camera. The sim owns whether it is raining; this only
// draws it. Particles recycle within a box around the camera so the count stays
// fixed regardless of how far the player travels.
import * as THREE from 'three';

export type Precip = 'none' | 'rain' | 'snow';

const COUNT = 700;
const BOX_XZ = 24; // half-extent of the spawn box around the camera
const TOP = 16; // spawn height above the camera
const BOTTOM = -10;

export class WeatherFX {
  private readonly geo = new THREE.BufferGeometry();
  private readonly positions = new Float32Array(COUNT * 3);
  private readonly speeds = new Float32Array(COUNT);
  private readonly points: THREE.Points;
  private readonly material: THREE.PointsMaterial;
  private current: Precip = 'none';

  constructor(scene: THREE.Scene) {
    for (let i = 0; i < COUNT; i++) {
      this.positions[i * 3] = (Math.random() - 0.5) * 2 * BOX_XZ;
      this.positions[i * 3 + 1] = Math.random() * (TOP - BOTTOM) + BOTTOM;
      this.positions[i * 3 + 2] = (Math.random() - 0.5) * 2 * BOX_XZ;
      this.speeds[i] = 0.4 + Math.random() * 0.5;
    }
    this.geo.setAttribute('position', new THREE.BufferAttribute(this.positions, 3));
    this.material = new THREE.PointsMaterial({ color: 0x9fb8e0, size: 0.12, transparent: true, opacity: 0.6, depthWrite: false });
    this.points = new THREE.Points(this.geo, this.material);
    this.points.frustumCulled = false;
    this.points.visible = false;
    scene.add(this.points);
  }

  update(camera: THREE.Camera, kind: Precip): void {
    if (kind !== this.current) {
      this.current = kind;
      this.points.visible = kind !== 'none';
      if (kind === 'snow') { this.material.color.setHex(0xf0f4ff); this.material.size = 0.18; }
      else if (kind === 'rain') { this.material.color.setHex(0x9fb8e0); this.material.size = 0.12; }
    }
    if (kind === 'none') return;
    const cx = camera.position.x, cy = camera.position.y, cz = camera.position.z;
    const rainMul = kind === 'snow' ? 0.35 : 1; // snow drifts slower
    for (let i = 0; i < COUNT; i++) {
      let y = this.positions[i * 3 + 1]! - this.speeds[i]! * rainMul;
      if (y < BOTTOM) {
        y = TOP;
        this.positions[i * 3] = (Math.random() - 0.5) * 2 * BOX_XZ;
        this.positions[i * 3 + 2] = (Math.random() - 0.5) * 2 * BOX_XZ;
      }
      this.positions[i * 3 + 1] = y;
    }
    // Reposition the whole cloud to sit on the camera (particles carry local coords).
    this.points.position.set(cx, cy, cz);
    this.geo.attributes.position!.needsUpdate = true;
  }

  dispose(): void {
    this.geo.dispose();
    this.material.dispose();
    this.points.removeFromParent();
  }
}
